import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Layout } from "@/components/layout/Layout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, ShieldAlert, BookOpen, LayoutGrid, Inbox, Link2, ArrowRight } from "lucide-react";
import { AdminSubmissions } from "@/components/admin/AdminSubmissions";
import { LeadTracker } from "@/components/admin/LeadTracker";

export default function Admin() {
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);

  useEffect(() => {
    const checkAdminAccess = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) { setIsAdmin(false); return; }

      const { data, error } = await supabase.rpc('has_role', { _user_id: session.user.id, _role: 'admin' });
      if (error) { console.error('Error checking admin role:', error); setIsAdmin(false); return; }
      setIsAdmin(data === true);
    };
    checkAdminAccess();
  }, []);

  if (isAdmin === null) {
    return (
      <Layout>
        <div className="flex min-h-screen items-center justify-center bg-background">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }

  if (isAdmin === false) {
    return (
      <Layout>
        <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4">
          <Card className="max-w-md text-center">
            <CardContent className="pt-6">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
                <ShieldAlert className="h-8 w-8 text-destructive" />
              </div>
              <h1 className="font-playfair text-2xl font-semibold text-foreground">Access Denied</h1>
              <p className="mt-2 text-muted-foreground">You don't have permission to access the admin dashboard.</p>
              <div className="mt-6 flex flex-col gap-2">
                <Button onClick={() => navigate("/login")} className="w-full">Sign In</Button>
                <Button variant="outline" onClick={() => navigate("/")} className="w-full">Go Home</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-background">
        <div className="container mx-auto max-w-6xl px-4 pt-24 pb-12">
          {/* Page header */}
          <div className="mb-8">
            <h1 className="font-playfair text-3xl font-semibold text-foreground md:text-4xl">
              Admin Dashboard
            </h1>
            <p className="mt-1 text-muted-foreground">
              Manage submissions, content, and client tracking
            </p>
          </div>

          {/* Tabbed sections */}
          <Tabs defaultValue="submissions" className="space-y-6">
            <TabsList className="grid w-full grid-cols-4 h-auto p-1">
              <TabsTrigger value="submissions" className="gap-1.5 text-xs sm:text-sm py-2.5">
                <Inbox className="h-4 w-4" />
                <span className="hidden sm:inline">Submissions</span>
              </TabsTrigger>
              <TabsTrigger value="leads" className="gap-1.5 text-xs sm:text-sm py-2.5">
                <Link2 className="h-4 w-4" />
                <span className="hidden sm:inline">Lead Tracker</span>
              </TabsTrigger>
              <TabsTrigger value="cases" className="gap-1.5 text-xs sm:text-sm py-2.5">
                <BookOpen className="h-4 w-4" />
                <span className="hidden sm:inline">Cases</span>
              </TabsTrigger>
              <TabsTrigger value="components" className="gap-1.5 text-xs sm:text-sm py-2.5">
                <LayoutGrid className="h-4 w-4" />
                <span className="hidden sm:inline">Components</span>
              </TabsTrigger>
            </TabsList>

            {/* Submissions tab */}
            <TabsContent value="submissions">
              <AdminSubmissions />
            </TabsContent>

            {/* Lead Tracker tab */}
            <TabsContent value="leads">
              <LeadTracker />
            </TabsContent>

            {/* Case Articles tab */}
            <TabsContent value="cases">
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
                    <BookOpen className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground">Case Articles</h3>
                  <p className="mt-1 max-w-sm text-sm text-muted-foreground">
                    Create, edit, and manage case study articles. Control publishing status and display order.
                  </p>
                  <Button asChild className="mt-6 gap-2">
                    <Link to="/admin/cases">
                      Open Case Editor <ArrowRight className="h-4 w-4" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Components tab */}
            <TabsContent value="components">
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
                    <LayoutGrid className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground">Components</h3>
                  <p className="mt-1 max-w-sm text-sm text-muted-foreground">
                    Manage your component library — add new items, update images, and organize categories.
                  </p>
                  <Button asChild className="mt-6 gap-2">
                    <Link to="/admin/components">
                      Open Component Editor <ArrowRight className="h-4 w-4" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </Layout>
  );
}
